import { useCallback, useEffect, useRef, useState } from "react";

import { tokenizeCompactJson } from "./format";
import type { JsonToken, ParsedDcMessage } from "./format";
import { isNearScrollBottom } from "./scroll";
import styles from "./OperatorLogs.module.scss";

interface DcLineProps {
  entry: ParsedDcMessage;
  expanded: boolean;
  onToggle: () => void;
}

function DcLine({ entry, expanded, onToggle }: DcLineProps) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(0);
  const preview = entry.preview ?? entry.body ?? "";
  const detail = entry.detail || preview;
  const tokens = tokenizeCompactJson(preview);
  const summary = `[${entry.time}] ${entry.tag} ${preview}`;

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const onCopyDetail = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      try {
        await copyTextToClipboard(detail);
        setCopied(true);
        if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.warn("[dc] copy failed:", err);
      }
    },
    [detail]
  );

  const renderToken = (t: JsonToken, i: number) => {
    if (t.type === "key")
      return (
        <span key={i} className={styles.payloadKey}>
          {t.value}
        </span>
      );
    if (t.type === "str")
      return (
        <span key={i} className={styles.payloadString}>
          {t.value}
        </span>
      );
    if (t.type === "num")
      return (
        <span key={i} className={styles.payloadNumber}>
          {t.value}
        </span>
      );
    return <span key={i}>{t.value}</span>;
  };

  const entryClass = [styles.entry, styles[entry.variant] ?? "", expanded ? styles.expanded : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={entryClass}>
      <button
        type="button"
        className={styles.entrySummary}
        title={expanded ? "Свернуть" : "Развернуть полную команду"}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className={styles.entryChevron} aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
        <span className={styles.entrySummaryText}>
          <span className={styles.timestamp}>[{entry.time}] </span>
          <span className={styles.tag}>{entry.tag} </span>
          <span className={styles.payload}>{tokens.map(renderToken)}</span>
        </span>
      </button>
      {expanded ? (
        <div className={styles.entryDetailWrap}>
          <div className={styles.entryDetailToolbar}>
            <span className={styles.entryDetailLabel}>Full message</span>
            <button
              type="button"
              className={styles.entryCopy}
              onClick={onCopyDetail}
              aria-label={`Copy ${summary}`}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className={styles.entryDetail}>{detail}</pre>
        </div>
      ) : null}
    </div>
  );
}

type DcTabId = "bridge" | "operator";

const DC_TABS: Array<{
  id: DcTabId;
  panel: string;
  label: string;
  hint: string;
}> = [
  {
    id: "bridge",
    panel: "socketio",
    label: "Data bridge",
    hint: "Команды управления (→ на робота) и ответы бекенда (← robot-config, error). Телеметрия и статус сюда не пишутся.",
  },
  {
    id: "operator",
    panel: "operator",
    label: "Исходящие (UI)",
    hint: "Команды, которые UI только что сформировал. Не гарантия доставки на робота / в Data bridge.",
  },
];

export interface DataChannelLogsProps {
  incoming?: ParsedDcMessage[];
  outgoing?: ParsedDcMessage[];
  /** Stretch the card to fill the parent column (ViewerPage aside). */
  fill?: boolean;
}

/**
 * Single card with tabs: Data bridge ↔ Исходящие (UI). Default tab: Data bridge.
 */
export function DataChannelLogs({
  incoming = [],
  outgoing = [],
  fill = false,
}: DataChannelLogsProps) {
  const [tab, setTab] = useState<DcTabId>("bridge");
  const active = DC_TABS.find((t) => t.id === tab) ?? DC_TABS[0];
  const lines = tab === "operator" ? outgoing : incoming;
  if (!active) return null;

  return (
    <div className={fill ? `${styles.card} ${styles.fill}` : styles.card}>
      <div className={styles.tabs} role="tablist" aria-label="DataChannel logs">
        {DC_TABS.map((t) => {
          const selected = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`dc-tab-${t.id}`}
              className={`${styles.tab}${selected ? ` ${styles.active}` : ""}`}
              aria-selected={selected}
              aria-controls={`dc-tab-panel-${t.id}`}
              title={t.hint}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div
        className={styles.cardBody}
        role="tabpanel"
        id={`dc-tab-panel-${active.id}`}
        aria-labelledby={`dc-tab-${active.id}`}
        data-dc-panel={active.panel}
      >
        <DcLogBody panel={active.panel} lines={lines} />
      </div>
    </div>
  );
}

interface DcLogBodyProps {
  panel: string;
  lines: ParsedDcMessage[];
}

function DcLogBody({ panel, lines }: DcLogBodyProps) {
  const logRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const el = logRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines, panel]);

  useEffect(() => {
    setExpandedId(null);
  }, [panel]);

  useEffect(() => {
    if (!expandedId) return;
    if (!lines.some((e) => e.id === expandedId)) {
      setExpandedId(null);
    }
  }, [expandedId, lines]);

  const onScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    stickToBottomRef.current = isNearScrollBottom(el, 48);
  }, []);

  const toggleEntry = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div
      className={styles.log}
      id={panel === "operator" ? "operator-dc-log" : "socketio-dc-log"}
      ref={logRef}
      onScroll={onScroll}
    >
      {lines.map((entry, i) => {
        const id = entry.id ?? `${entry.time}-${i}`;
        return (
          <DcLine
            key={id}
            entry={entry}
            expanded={expandedId === id}
            onToggle={() => toggleEntry(id)}
          />
        );
      })}
    </div>
  );
}

function formatDiagFilename(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const mo = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const h = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const s = pad(date.getSeconds());
  return `operator-ui-diag-${y}${mo}${d}-${h}${mi}${s}.txt`;
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("execCommand copy failed");
    }
  } finally {
    document.body.removeChild(ta);
  }
}

function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface SystemLogPanelProps {
  lines?: string[];
}

/**
 * System/diagnostics log panel (plain lines + copy/save).
 */
export function SystemLogPanel({ lines = [] }: SystemLogPanelProps) {
  const preRef = useRef<HTMLPreElement | null>(null);
  const stickToBottomRef = useRef(true);
  const copiedTimerRef = useRef(0);
  const [copied, setCopied] = useState(false);
  const empty = !lines?.length;
  const text = empty ? "" : lines.join("\n");

  useEffect(() => {
    const el = preRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const onScroll = useCallback(() => {
    const el = preRef.current;
    if (!el) return;
    stickToBottomRef.current = isNearScrollBottom(el, 48);
  }, []);

  const onCopy = useCallback(async () => {
    if (empty) return;
    try {
      await copyTextToClipboard(text);
      setCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[diag] copy failed:", err);
    }
  }, [empty, text]);

  const onSave = useCallback(() => {
    if (empty) return;
    downloadTextFile(formatDiagFilename(), text);
  }, [empty, text]);

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Diagnostics</h2>
        <div className={styles.panelActions}>
          <button
            type="button"
            className={styles.panelAction}
            disabled={empty}
            onClick={onCopy}
            aria-label="Copy diagnostics log"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            className={styles.panelAction}
            disabled={empty}
            onClick={onSave}
            aria-label="Save diagnostics log"
          >
            Save
          </button>
        </div>
      </div>
      <pre id="diag-log" className={styles.diagLog} ref={preRef} onScroll={onScroll}>
        {text}
      </pre>
    </section>
  );
}
